import React, { useEffect, useState } from 'react';
import Api from '../../Api';
import { withAuth } from '../../hoc/Auth';
import { Column, Container, Wrapper } from '../../hoc/Layout';
import ProjectsFilters from '../../Components/ComponentsOfAdministrator/ProjectsFilters';
import ResponsiveSidebarFilters from '../../Components/Utils/ResponsiveSidebarFilters';
import AdminFiltersToggle from '../../Components/ComponentsOfAdministrator/AdminFiltersToggle';
import { FormattedMessage } from 'react-intl';
import Icon from '../../Components/Utils/Icon';
import ProjectsList from '../../Components/ComponentsOfAdministrator/ProjectsList';
import Pagination from 'react-js-pagination';
import DateRange from '../../Components/Utils/DateRange';
import Modal from '../../Components/Utils/Modal';
import ProjectEditing from '../../Components/ComponentsOfAdministrator/ProjectEditing';
import ProjectAdd from '../../Components/ComponentsOfAdministrator/ProjectAdd';
import EmptyCard from '../../hoc/EmptyCard';
import Search from '../../Components/Utils/SearchTags';

import classes from './styles.module.css';
import filtersClasses from '../../Components/ComponentsOfAdministrator/AdminFiltersToggle/styles.module.css';
import Context from '../../Context';

const Projects = withAuth(({ token }) => {
    const [isOpenModal, setIsOpenModal] = useState(false);

    const [currentProjectsPage, setCurrentProjectsPage] = useState(1);
    const [postsPerProjectsPage, setPostsPerProjectsPage] = useState(10);
    const [projectsListCount, setProjectsListCount] = useState(0);

    const [projectsList, setProjectsList] = useState([]);
    const [projectData, setProjectData] = useState({});

    const [currentProjectUuid, setCurrentProjectUuid] = useState(null);

    const [projectsSort] = useState('DESC');
    const [filterDate, setFilterDate] = useState({});
    const [filters, setFilters] = useState([]);
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);

    const [loadingProjectsList, setLoadingProjectsList] = useState(true);

    const [users, setUsers] = useState(null);

    const loadProjectsList = () => {
        const payload = {
            page: currentProjectsPage,
            itemsPerPage: postsPerProjectsPage,
            order: projectsSort,
            dateRange: filterDate,
            filters: filters,
        };

        setLoadingProjectsList(true);

        new Api({ token })
            .getAccountsList({
                page: 1,
                itemsPerPage: 1000,
                order: 'DESC',
            })
            .then((r) => {
                setUsers(r.data);
            });

        new Api({ token }).getProjectsList(payload).then((r) => {
            setProjectsList(r.data);
            setProjectsListCount(r.count);
            setLoadingProjectsList(false);
        });
    };

    useEffect(() => {
        loadProjectsList();
    }, [filters, filterDate, currentProjectsPage, postsPerProjectsPage, projectsSort]);

    function setPage(page, itemsPerPage) {
        setCurrentProjectsPage(page || 1);
        if (itemsPerPage) {
            setPostsPerProjectsPage(itemsPerPage);
        }
    }

    function onSelectProjectUuid(uuid) {
        setCurrentProjectUuid(uuid);

        new Api({ token }).getProjectData(uuid).then((r) => {
            setProjectData(r.info);
        });
    }

    function onProjectAdd() {
        setCurrentProjectsPage(1);
        setIsOpenModal(false);
        loadProjectsList();
    }

    const handleProjectSaved = () => {
        loadProjectsList();
    };

    return (
            <Wrapper>
            <Modal
                title={<FormattedMessage id="Projects.modal.addTitle" defaultMessage="Добавить новый проект" />}
                onToggleModal={() => setIsOpenModal(!isOpenModal)}
                isOpen={isOpenModal}
                onClose={() => setIsOpenModal(false)}
            >
                <ProjectAdd token={token} onProjectAdd={onProjectAdd} />
            </Modal>

            <Context.Provider value={{ onSelectProjectUuid }}>
                <Container>
                    <Column width="780" className={filtersClasses.MainColumn}>
                        <AdminFiltersToggle onClick={() => setIsFiltersOpen(true)} />
                        <Search
                            onChangeSearch={(tags) => {
                                const clean = tags.map((tag) => {
                                    const clone = { ...tag };
                                    delete clone.locale;
                                    return clone;
                                });
                                setFilters(clean);
                                setCurrentProjectsPage(1);
                            }}
                            onSearch={() => loadProjectsList()}
                            isDisabled={false}
                            defaultSearch={{
                                key: 'name',
                                locale: 'Search.label.projectName',
                                placeholder: 'Search.placeholder.projectName',
                            }}
                            searchBy={[
                                {
                                    key: 'name',
                                    locale: 'Search.label.projectName',
                                    placeholder: 'Search.placeholder.projectName',
                                },
                                { key: 'url', locale: 'Search.label.url', placeholder: 'Search.placeholder.url' },
                                {
                                    key: 'description',
                                    locale: 'Search.label.description',
                                    placeholder: 'Search.placeholder.description',
                                },
                                { key: 'uuid', locale: 'Search.label.uuid', placeholder: 'Search.placeholder.uuid' },
                            ]}
                        />

                        <div className={classes.Card}>
                            <div className={classes.Header}>
                                <h5 className={classes.Title}>
                                    <FormattedMessage id="ProjectsControl.title.projects" defaultMessage="Проекты" />
                                </h5>

                                <div className={classes.Btn} onClick={() => setIsOpenModal(!isOpenModal)}>
                                    <Icon className={classes.IconLeft} icon="add-outline" size={'16px'} />
                                    <FormattedMessage id="Projects.button.add" defaultMessage="Добавить" />
                                </div>
                            </div>

                            <ProjectsList
                                isLoading={loadingProjectsList}
                                data={projectsList}
                                currentUuid={currentProjectUuid}
                            />

                            {postsPerProjectsPage < projectsListCount && (
                                <div className={classes.PaginationWrap}>
                                    <Pagination
                                        firstPageText="⇤"
                                        prevPageText="⇠"
                                        lastPageText="⇥"
                                        nextPageText="⇢"
                                        activeLinkClass={classes.ActiveLink}
                                        activePage={currentProjectsPage}
                                        itemsCountPerPage={postsPerProjectsPage}
                                        totalItemsCount={projectsListCount}
                                        pageRangeDisplayed={5}
                                        onChange={(page) => setPage(page, postsPerProjectsPage)}
                                    />
                                </div>
                            )}
                        </div>

                        <ProjectEditing
                            project={projectData}
                            token={token}
                            uuid={currentProjectUuid}
                            usersList={users}
                            onProjectSaved={handleProjectSaved}
                        />
                    </Column>

                    <Column width="350" className={filtersClasses.SidebarColumn}>
                        <ResponsiveSidebarFilters
                            title="Фильтры"
                            isOpen={isFiltersOpen}
                            onClose={() => setIsFiltersOpen(false)}
                        >
                            <EmptyCard>
                                <DateRange
                                    onSelectDate={(range) => {
                                        setFilterDate(range || {});
                                        setCurrentProjectsPage(1);
                                    }}
                                    defaultPeriod={'zero'}
                                />
                            </EmptyCard>

                            <ProjectsFilters
                                isLoading={loadingProjectsList}
                                onApplyFilters={(newFilters) => {
                                    setFilters(newFilters || []);
                                    setCurrentProjectsPage(1);
                                    setIsFiltersOpen(false);
                                }}
                            />
                        </ResponsiveSidebarFilters>
                    </Column>
                </Container>
            </Context.Provider>
        </Wrapper>
    );
});

export default Projects;



// WEBPACK FOOTER //
// ./src/PagesOfAdministrator/Ptojects/index.js